import * as React from "react";
import {
  Checkbox,
  Body1,
  Caption1,
  tokens,
  makeStyles,
} from "@fluentui/react-components";
import { Attach20Regular } from "@fluentui/react-icons";
import { MailAttachmentInfo } from "../services/outlookContext";

interface Props {
  attachments: MailAttachmentInfo[];
  /** IDs des attachments sélectionnés pour upload. */
  selected: string[];
  onChange: (selected: string[]) => void;
}

const useStyles = makeStyles({
  root: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    paddingTop: "2px",
    paddingBottom: "2px",
  },
  size: {
    color: tokens.colorNeutralForeground3,
  },
});

function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export function AttachmentsBlock({
  attachments,
  selected,
  onChange,
}: Props): React.ReactElement | null {
  const styles = useStyles();

  // On masque les pièces jointes inline (images intégrées dans le HTML du mail)
  // pour éviter de polluer le ticket avec des logos / signatures.
  const visible = attachments.filter((a) => !a.isInline);
  if (visible.length === 0) return null;

  const allSelected = visible.every((a) => selected.includes(a.id));
  const noneSelected = visible.every((a) => !selected.includes(a.id));

  function toggleAll(): void {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(visible.map((a) => a.id));
    }
  }

  function toggleOne(id: string): void {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Attach20Regular />
        <Body1>
          <strong>Pièces jointes du mail ({visible.length})</strong>
        </Body1>
      </div>

      <Checkbox
        checked={allSelected ? true : noneSelected ? false : "mixed"}
        onChange={toggleAll}
        label={allSelected ? "Tout désélectionner" : "Tout sélectionner"}
      />

      {visible.map((a) => (
        <div key={a.id} className={styles.item}>
          <Checkbox
            checked={selected.includes(a.id)}
            onChange={() => toggleOne(a.id)}
            label={
              <span>
                {a.name} <Caption1 className={styles.size}>({formatSize(a.size)})</Caption1>
              </span>
            }
          />
        </div>
      ))}
    </div>
  );
}
